import type { ClientRequest, ClientResponse, Zone, ZoneDraft, ZoneUpdateAction } from '@commercetools/platform-sdk';

import type { ApiClientDetails, QueryArgs } from '../../types';
import { getQueryPredicate, QueryKey } from '../utils';

export function zoneApi({ apiRoot, ctpClient }: ApiClientDetails) {
    function zoneClientRequest(args?: QueryArgs): ClientRequest {
        return apiRoot().zones().get(args).clientRequest();
    }

    function fetchZones(args?: QueryArgs): Promise<Zone[]> {
        const clientRequest = zoneClientRequest(args);

        return ctpClient().process(clientRequest, payload => Promise.resolve(payload.body.results), {
            accumulate: true,
        });
    }

    function fetchAllZones(expand?: string | string[]): Promise<Zone[]> {
        return fetchZones({ queryArgs: { expand } });
    }

    function getZonesByIds(ids: string[]): Promise<Zone[]> {
        return fetchZones({
            queryArgs: { where: getQueryPredicate(ids, QueryKey.ID) },
        });
    }

    async function getZoneById(id: string): Promise<Zone> {
        return (await apiRoot().zones().withId({ ID: id }).get().execute()).body;
    }

    async function createZone(zone: ZoneDraft): Promise<ClientResponse<Zone>> {
        return apiRoot().zones().post({ body: zone }).execute();
    }

    async function getZoneByKey(key: string): Promise<Zone> {
        const zone = await apiRoot()
            .zones()
            .withKey({
                key,
            })
            .get()
            .execute();

        return zone.body;
    }

    function updateZone(key: string, version: number, actions: ZoneUpdateAction[]): Promise<ClientResponse<Zone>> {
        return apiRoot()
            .zones()
            .withKey({ key })
            .post({
                body: {
                    version,
                    actions,
                },
            })
            .execute();
    }

    return {
        fetchAllZones,
        getZonesByIds,
        getZoneById,
        createZone,
        getZoneByKey,
        updateZone,
    };
}
